import CenteredSpinner from "@/components/CenteredSpinner";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import useOrganization from "@/hooks/useOrganization";
import { useToast } from "@/hooks/utils/useToast";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LicensesRecapDashboard from "./components/LicensesRecapDashboard";
import UserNotification, {
    Notification,
    NotificationType,
} from "./components/UserNotification";
import UserTable from "./components/UserTable";
import { NONE_LICENSE_VALUE } from "./types";
import { buildLicensesRecap } from "./utils/organizationLicenseUtils";
import { buildUsersWithLicense } from "./utils/userManagementUserUtils";

const UserManagementSettings = () => {
    const { t } = useTranslation();
    const {
        adminCount,
        affectLicense,
        currentUserIsAdmin,
        isError,
        isLoading,
        isUserAdmin,
        organization,
        organizationUsers,
        removeLicense,
        setAdmin,
    } = useOrganization({ fetchUsers: true });
    const { data: currentUser } = useGetUserProfile();
    const { toast } = useToast();
    const [notification, setNotification] = useState<Notification>();

    // Users are derived from 2 TanStack queries (org + users). After each mutation they are
    // kept in sync by an optimistic update reconciled with the SDK post-mutation snapshot
    // (see useOrganization) — no refetch.
    const users = useMemo(
        () =>
            buildUsersWithLicense({
                adminCount,
                isUserAdmin,
                organization,
                organizationUsers,
            }),
        [adminCount, isUserAdmin, organization, organizationUsers],
    );

    const licensesRecapData = useMemo(
        () => buildLicensesRecap(organization?.licenses),
        [organization?.licenses],
    );

    const licenseOptions = useMemo(
        () =>
            licensesRecapData.map((license) => ({
                label: license.displayName,
                value: license.id,
                available: license.available,
            })),
        [licensesRecapData],
    );

    const handleToggleAdmin = async (userId: string, isAdmin: boolean) => {
        const user = users.find((entry) => entry.userId === userId);

        if (!currentUser || !user) {
            toast({
                title: t("settings.userManagement.error.roleChanged"),
                variant: "error",
            });

            return;
        }

        if (user.isLastAdmin && !isAdmin) {
            toast({
                title: t("settings.userManagement.error.adminSelf"),
                variant: "error",
            });

            return;
        }

        try {
            await setAdmin({
                userId: user.userId,
                userMail: user.userMail || "",
                isAdmin,
            });
            setNotification({
                type: NotificationType.ROLE_CHANGED,
                email: user.userMail || "",
            });
        } catch {
            toast({
                title: t("settings.userManagement.error.roleChanged"),
                variant: "error",
            });
        }
    };

    const handleToggleLicense = async (
        userId: string,
        newLicenseId: string,
    ) => {
        const user = users.find((entry) => entry.userId === userId);

        if (!currentUser || !user || !user.userMail) {
            toast({
                title: t("settings.userManagement.error.toggleLicense"),
                variant: "error",
            });

            return;
        }

        const isRemoval = newLicenseId === NONE_LICENSE_VALUE;

        if (isRemoval && user.isLastAdmin) {
            toast({
                title: t("settings.userManagement.error.licenseSelf"),
                variant: "error",
            });

            return;
        }

        try {
            if (isRemoval) {
                if (!user.licenseId) {
                    return;
                }

                await removeLicense({
                    userId: user.userId,
                    userMail: user.userMail,
                    orgLicenseId: user.licenseId,
                });
            } else {
                await affectLicense({
                    licenseId: newLicenseId,
                    user: {
                        userId: user.userId,
                        userMail: user.userMail,
                    },
                });
            }

            setNotification({
                type: NotificationType.LICENSE_CHANGED,
                email: user.userMail,
            });
        } catch {
            toast({
                title: t("settings.userManagement.error.toggleLicense"),
                variant: "error",
            });
        }
    };

    if (isLoading) {
        return <CenteredSpinner />;
    }

    if (isError) {
        return (
            <p className="p-4 text-sm text-error-base">
                {t("settings.userManagement.error.loadOrganization")}
            </p>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col">
            <DialogHeader className="shrink-0">
                <DialogTitle className="font-bold leading-none">
                    {t("settings.userManagement.title")}
                </DialogTitle>
                <p className="text-xs">
                    {t("settings.userManagement.description")}
                </p>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto py-5">
                <LicensesRecapDashboard licenses={licensesRecapData} />

                <div className="mt-6 overflow-x-auto">
                    <UserTable
                        canEdit={currentUserIsAdmin}
                        licenseOptions={licenseOptions}
                        onToggleAdmin={handleToggleAdmin}
                        onToggleLicense={handleToggleLicense}
                        users={users}
                    />
                </div>
            </div>

            <footer className="shrink-0 space-y-3 border-t border-border bg-background pt-4 pb-2">
                <UserNotification notification={notification} />
                <p className="text-text-secondary text-xs leading-relaxed">
                    {t("settings.userManagement.licenses.reassignNote")}
                </p>
            </footer>
        </div>
    );
};

export default UserManagementSettings;
